import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import ErrorBoundary from '@/components/common/ErrorBoundary'
import { useLocationManager } from '@/hooks/useLocationManager'
import { useCartSync } from '@/hooks/useCartSync'
import { AuthProvider } from '@/contexts/AuthContext'

// Page components
import { HomePage } from '@/pages/HomePage'
import { ResponsiveAddressSelectPage } from '@/pages/AddressSelectPage'
import { ResponsiveAddressDetailPage } from '@/pages/AddressDetailPage'
import { ResponsiveMapLocationSelectPage } from '@/pages/MapLocationSelectPage'
import { ResponsiveViewFilterPage } from '@/pages/ViewFilterPage'
import { ResponsiveSearchPage } from '@/pages/SearchPage'
import { ResponsiveStoreDetailPage } from '@/pages/ResponsiveStoreDetailPage'
import { ResponsiveConsumerStoreReviewsPage } from '@/pages/ResponsiveConsumerStoreReviewsPage'
import { ResponsiveSellerStoreReviewsPage } from '@/pages/ResponsiveSellerStoreReviewsPage'
import { ResponsiveStoreInfoPage } from '@/pages/ResponsiveStoreInfoPage'
import { ResponsiveProductDetailPage } from '@/pages/ResponsiveProductDetailPage'
import { ResponsiveFavoritesPage } from '@/pages/ResponsiveFavoritesPage'
import { ResponsiveNotificationPage } from '@/pages/NotificationPage'
import { ResponsiveMyPage } from '@/pages/ResponsiveMyPage'
import { ResponsiveLoginPage } from '@/pages/ResponsiveLoginPage'
import { ResponsiveSignupSelectPage } from '@/pages/ResponsiveSignupSelectPage'
import { ResponsiveBuyerSignupPage } from '@/pages/ResponsiveBuyerSignupPage'
import { ResponsiveFindAccountPage } from '@/pages/ResponsiveFindAccountPage'
import { ResponsiveFindIdPage } from '@/pages/ResponsiveFindIdPage'
import { ResponsiveResetPasswordPage } from '@/pages/ResponsiveResetPasswordPage'
import { ResponsiveSellerSignupPage } from '@/pages/ResponsiveSellerSignupPage'
import { ResponsiveSellerBusinessInfoPage } from '@/pages/ResponsiveSellerBusinessInfoPage'
import { ResponsiveSellerStoreInfoPage } from '@/pages/ResponsiveSellerStoreInfoPage'
import { ResponsiveSellerSignupCompletePage } from '@/pages/ResponsiveSellerSignupCompletePage'
import { ResponsiveAuthConfirmPage } from '@/pages/ResponsiveAuthConfirmPage'
import { ResponsiveSignupSuccessPage } from '@/pages/ResponsiveSignupSuccessPage'
import { ResponsiveCartPage } from '@/pages/CartPage'
import { ResponsiveCheckoutPage } from '@/pages/ResponsiveCheckoutPage'
import { ResponsiveOrderHistoryPage } from '@/pages/ResponsiveOrderHistoryPage'
import { ResponsiveReviewWritePage } from '@/pages/ResponsiveReviewWritePage'
import { ResponsiveNoticesPage } from '@/pages/ResponsiveNoticesPage'
import { ResponsiveTermsPoliciesPage } from '@/pages/ResponsiveTermsPoliciesPage'
import { ResponsiveTermsPolicyDetailPage } from '@/pages/ResponsiveTermsPolicyDetailPage'
import { ResponsiveServiceInfoPage } from '@/pages/ResponsiveServiceInfoPage'
import { ResponsiveEventsPage } from '@/pages/ResponsiveEventsPage'
import { ResponsiveEventDetailPage } from '@/pages/ResponsiveEventDetailPage'
import { ResponsiveSettingsPage } from '@/pages/ResponsiveSettingsPage'


// Seller pages
import { ResponsiveSellerHomePage } from '@/pages/ResponsiveSellerHomePage'
import { ResponsiveOrderDetailPage } from '@/pages/ResponsiveOrderDetailPage'
import { ResponsiveOrderAcceptPage } from '@/pages/ResponsiveOrderAcceptPage'
import { ResponsiveOrderCompletePage } from '@/pages/ResponsiveOrderCompletePage'
import { ResponsiveOrderRejectReasonPage } from '@/pages/ResponsiveOrderRejectReasonPage'

// Payment pages
import { ResponsivePaymentSuccessPage } from '@/pages/ResponsivePaymentSuccessPage'
import { ResponsivePaymentFailPage } from '@/pages/ResponsivePaymentFailPage'
import { ResponsiveOrderRefundReasonPage } from '@/pages/ResponsiveOrderRefundReasonPage'
import { ResponsiveMyStorePage } from '@/pages/ResponsiveMyStorePage'
import { ResponsiveSalesHistoryPage } from '@/pages/ResponsiveSalesHistoryPage'
import { ResponsiveDeadlineDiscountPage } from '@/pages/ResponsiveDeadlineDiscountPage'
import { ResponsiveReservationDiscountPage } from '@/pages/ResponsiveReservationDiscountPage'
import { ResponsiveProductRegisterPage } from '@/pages/ResponsiveProductRegisterPage'
import { ResponsiveProductEditPage } from '@/pages/ResponsiveProductEditPage'
import { ResponsiveStoreEditPage } from '@/pages/ResponsiveStoreEditPage'
import { ResponsiveOperatingHoursPage } from '@/pages/ResponsiveOperatingHoursPage'
import { ResponsiveStoreMonitoringPage } from '@/pages/ResponsiveStoreMonitoringPage'
import { ResponsiveSellerMyPage } from '@/pages/ResponsiveSellerMyPage'
import { ResponsiveSellerEventsPage } from '@/pages/ResponsiveSellerEventsPage'
import { ResponsiveSellerNoticesPage } from '@/pages/ResponsiveSellerNoticesPage'
import { ResponsiveSellerSettingsPage } from '@/pages/ResponsiveSellerSettingsPage'

// Simple app component - no auth for now, just home screen

function AppContent() {
  // 앱 시작 시 위치 데이터 초기화
  useLocationManager();
  
  // 장바구니 동기화
  useCartSync();

  return (
    <Router>
          <Routes>
            {/* Main home screen route */}
            <Route path="/" element={<HomePage />} />
            
            {/* Address management routes */}
            <Route path="/address-select" element={<ResponsiveAddressSelectPage />} />
            <Route path="/address-detail" element={<ResponsiveAddressDetailPage />} />
            <Route path="/map-location-select" element={<ResponsiveMapLocationSelectPage />} />
            
            {/* Filter page */}
            <Route path="/view-filter" element={<ResponsiveViewFilterPage />} />
            
            {/* Search page */}
            <Route path="/buyer/search" element={<ResponsiveSearchPage />} />
            
            {/* Notification page */}
            <Route path="/buyer/notifications" element={<ResponsiveNotificationPage />} />
            
            {/* Authentication routes */}
            <Route path="/login" element={<ResponsiveLoginPage />} />
            <Route path="/signup-select" element={<ResponsiveSignupSelectPage />} />
            <Route path="/auth/confirm" element={<ResponsiveAuthConfirmPage />} />
            <Route path="/signup/buyer" element={<ResponsiveBuyerSignupPage />} />
            <Route path="/signup/success" element={<ResponsiveSignupSuccessPage />} />
            <Route path="/signup/seller" element={<ResponsiveSellerSignupPage />} />
            <Route path="/signup/seller/business" element={<ResponsiveSellerBusinessInfoPage />} />
            <Route path="/signup/seller/store" element={<ResponsiveSellerStoreInfoPage />} />
            <Route path="/signup/seller/complete" element={<ResponsiveSellerSignupCompletePage />} />
            <Route path="/find-account" element={<ResponsiveFindAccountPage />} />
            <Route path="/find-id" element={<ResponsiveFindIdPage />} />
            <Route path="/reset-password" element={<ResponsiveResetPasswordPage />} />
            
            {/* Store detail routes */}
            <Route path="/store/:storeId" element={<ResponsiveStoreDetailPage />} />
            <Route path="/store/:storeId/reviews" element={<ResponsiveConsumerStoreReviewsPage />} />
            <Route path="/store/:storeId/info" element={<ResponsiveStoreInfoPage />} />
            <Route path="/store/:storeId/product/:productId" element={<ResponsiveProductDetailPage />} />
            
            {/* Favorites page */}
            <Route path="/favorites" element={<ResponsiveFavoritesPage />} />
            
            {/* MyPage route */}
            <Route path="/mypage" element={<ResponsiveMyPage />} />
            
            {/* Settings page */}
            <Route path="/settings" element={<ResponsiveSettingsPage />} />
            
            {/* Cart page */}
            <Route path="/cart" element={<ResponsiveCartPage />} />
            
            {/* Checkout page */}
            <Route path="/checkout" element={<ResponsiveCheckoutPage />} />
            <Route path="/payment/success" element={<ResponsivePaymentSuccessPage />} />
            <Route path="/payment/fail" element={<ResponsivePaymentFailPage />} />
            
            {/* Order history page */}
            <Route path="/buyer/orders" element={<ResponsiveOrderHistoryPage />} />
            
            {/* Review write page */}
            <Route path="/buyer/orders/:orderId/review" element={<ResponsiveReviewWritePage />} />
            
            {/* MyPage 관련 서브 페이지들 */}
            <Route path="/mypage/notices" element={<ResponsiveNoticesPage />} />
            <Route path="/mypage/terms-policies" element={<ResponsiveTermsPoliciesPage />} />
            <Route path="/mypage/terms-policies/:id" element={<ResponsiveTermsPolicyDetailPage />} />
            <Route path="/mypage/terms-policies/title/:title" element={<ResponsiveTermsPolicyDetailPage />} />
            <Route path="/mypage/service-info" element={<ResponsiveServiceInfoPage />} />
            <Route path="/mypage/events" element={<ResponsiveEventsPage />} />
            <Route path="/mypage/events/:id" element={<ResponsiveEventDetailPage />} />
            
            {/* Seller routes */}
            <Route path="/seller/home" element={<ResponsiveSellerHomePage />} />
            <Route path="/seller/store" element={<ResponsiveMyStorePage />} />
            <Route path="/seller/store/sales-history" element={<ResponsiveSalesHistoryPage />} />
            <Route path="/seller/store/deadline-discount" element={<ResponsiveDeadlineDiscountPage />} />
            <Route path="/seller/store/reservation-discount" element={<ResponsiveReservationDiscountPage />} />
            <Route path="/seller/store/product-register" element={<ResponsiveProductRegisterPage />} />
            <Route path="/seller/store/product-edit" element={<ResponsiveProductEditPage />} />
            <Route path="/seller/orders/:orderId" element={<ResponsiveOrderDetailPage />} />
            <Route path="/seller/orders/:orderId/accept" element={<ResponsiveOrderAcceptPage />} />
            <Route path="/seller/orders/:orderId/complete" element={<ResponsiveOrderCompletePage />} />
            <Route path="/seller/orders/:orderId/reject" element={<ResponsiveOrderRejectReasonPage />} />
            <Route path="/seller/orders/:orderId/refund" element={<ResponsiveOrderRefundReasonPage />} />
            
            {/* Seller store management routes */}
            <Route path="/seller/store-edit" element={<ResponsiveStoreEditPage />} />
            <Route path="/seller/operating-hours" element={<ResponsiveOperatingHoursPage />} />
            <Route path="/seller/store-reviews" element={<ResponsiveSellerStoreReviewsPage />} />
            <Route path="/seller/store-monitoring" element={<ResponsiveStoreMonitoringPage />} />
            
            {/* Seller MyPage routes */}
            <Route path="/seller/mypage" element={<ResponsiveSellerMyPage />} />
            <Route path="/seller/settings" element={<ResponsiveSellerSettingsPage />} />
            <Route path="/seller/mypage/events" element={<ResponsiveSellerEventsPage />} />
            <Route path="/seller/mypage/notices" element={<ResponsiveSellerNoticesPage />} />
            <Route path="/seller/mypage/terms-policies" element={<ResponsiveTermsPoliciesPage />} />
            <Route path="/seller/mypage/service-info" element={<ResponsiveServiceInfoPage />} />
            
            {/* More routes will be added later */}
          </Routes>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('App Error:', error, errorInfo);
      }}
    >
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App